import { getMonth, getYear } from 'date-fns';
import { range } from 'lodash';
import React, { forwardRef, useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { FaCalendarAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import './style.scss';

interface DatePickerCustomProps {
  onChange?: (date: Date) => void;
  onInPut?: (date: string) => void;
  onBlur?: (date: Date) => void;
  value: Date | null;
  name?: string;
  maxDate?: Date | null;
  minDate?: Date | null;
  isInvalid?: boolean;
  errors?: string[] | undefined;
  disabled?: boolean;
  index?: number;
  key?: string;
}

const DatePickerCustom: React.FC<DatePickerCustomProps> = (props) => {
  const years = range(1900, 2100, 1);
  const months = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];

  const timer = useRef<number | null>(null);

  const handleChangeDate = (date: Date) => {
    if (props.onChange && date) {
      props.onChange(date);
    }
  };

  const handleInputChangeDate = (date: string) => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = window.setTimeout(() => {
      try {
        if (props.onInPut) {
          props.onInPut(date);
        }
      } catch (error) {
        console.error(error);
      }
      timer.current = null;
    }, 1000);
  };

  const handleBlur = (date: Date) => {
    try {
      if (props.onBlur) {
        props.onBlur(date);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <DatePicker
        className="form-control"
        dateFormat="yyyy/MM/dd"
        locale={'ja'}
        maxDate={props.maxDate ?? new Date('2099/12/31')}
        minDate={props.minDate ?? new Date('1990/01/01')}
        selected={props.value}
        //showIcon
        //icon={<FaCalendarAlt />}
        customInput={
          <CustomInputDatePicker
            isInvalid={props.isInvalid}
            errors={props.errors}
            index={props.index}
            handleInput={handleInputChangeDate}
            handleBlur={handleBlur}
          />
        }
        toggleCalendarOnIconClick
        onChange={handleChangeDate}
        name={props.name}
        popperContainer={({ children }) => <div>{children}</div>} // Renders outside table constraints
        disabled={props.disabled}
        popperPlacement="auto-start"
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <button
              className="btn btn-sm btn-primary"
              style={{ minWidth: '40px', backgroundColor: 'transparent', border: 'none' }}
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
            >
              {!prevMonthButtonDisabled && <FaChevronLeft size={14} className="fa-chevron-icon" />}
            </button>

            <Form.Select
              className="form-select form-select-sm"
              size="sm"
              value={months[getMonth(date)]}
              onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}
            >
              {months.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              className="form-select form-select-sm"
              size="sm"
              value={getYear(date)}
              onChange={({ target: { value } }) => changeYear(parseInt(value))}
            >
              {years.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Select>

            <button
              className="btn btn-sm btn-primary"
              style={{ minWidth: '40px', backgroundColor: 'transparent', border: 'none' }}
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
            >
              {!nextMonthButtonDisabled && <FaChevronRight size={14} className="fa-chevron-icon" />}
            </button>
          </div>
        )}
      />
    </>
  );
};

const CustomInputDatePicker = forwardRef((props: any, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (inputRef.current && props.value !== '') {
      inputRef.current.value = props.value;
    }
  }, [props.value]);

  const onIconCalendarClick = () => {
    props.onClick();
    inputRef.current?.focus();
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (props.handleInput) {
      props.handleInput(inputValue);
    }
  };

  const handleBlur = (e: any) => {
    const inputValue = e.target.value;
    if (props.handleBlur) {
      props.handleBlur(inputValue);
    }
  };
  // You can style this further
  return (
    <>
      <div className="position-relative">
        <FaCalendarAlt
          style={{
            position: 'absolute',
            top: '0.65rem',
            left: '0.8rem',
            cursor: 'pointer',
            padding: '0px',
            fontSize: '1rem',
          }}
          onClick={onIconCalendarClick}
        />
        <Form.Control
          className={props.className}
          onInput={onInputChange}
          onBlur={handleBlur}
          placeholder="YYYYMMDD"
          style={{ paddingLeft: '32px' }}
          type="text"
          isInvalid={props.isInvalid}
          name={props.name}
          readOnly={props.disabled}
          ref={inputRef}
        />
        {props.isInvalid && (
          <Form.Control.Feedback key={props.index} type="invalid">
            {props.errors}
          </Form.Control.Feedback>
        )}
      </div>
    </>
  );
});

export default DatePickerCustom;
